import { Injectable, OnDestroy } from '@angular/core';
import { AlertComponent, ToastService } from '@vsolv/vectors-ui/alert';
import { SvgIconRegistryService } from 'angular-svg-icon';
import { EMPTY, Subscription, catchError, debounce, distinctUntilChanged, first, of, timer } from 'rxjs';
import { CONNECTIVITY_CONFIG } from '../connectivity.config';
import { ConnectivityService } from './connectivity.service';

/** Icons the toasts render, fetched lazily by svg-icon - preloaded so the offline toast isn't iconless. */
const TOAST_ICONS = ['alert-triangle', 'check-circle', 'x-close'];

/** Turns connectivity changes into toasts. Started once from ConnectivityModule.forRoot(). */
@Injectable()
export class ConnectivityToastService implements OnDestroy {
  private readonly config = CONNECTIVITY_CONFIG;

  private subscription?: Subscription;
  private offlineToast?: AlertComponent;

  /** Tracks whether we actually went offline, so we don't greet everyone with "Back online" on boot. */
  private wasOffline = false;

  constructor(
    private connectivitySvc: ConnectivityService,
    private toastSvc: ToastService,
    private iconRegistry: SvgIconRegistryService
  ) {}

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  start() {
    if (this.subscription) return;

    this.preloadIcons();

    this.subscription = this.connectivitySvc.online$
      .pipe(
        // Offline shows instantly, but "online" only counts once it has held for a bit -
        // a flapping connection would otherwise spam toast pairs.
        debounce(online => (online ? timer(this.config.onlineStabilityDelay) : of(0))),
        distinctUntilChanged()
      )
      .subscribe(online => (online ? this.showBackOnline() : this.showOffline()));
  }

  /** Warm the icon cache while we're still online - once offline it's too late to fetch the svg. */
  private preloadIcons() {
    for (const icon of TOAST_ICONS) {
      this.iconRegistry
        .loadSvg(`assets/icons/line/${icon}.svg`)
        ?.pipe(catchError(() => EMPTY))
        .subscribe();
    }
  }

  private showOffline() {
    this.wasOffline = true;
    if (this.offlineToast) return;

    const toast = this.toastSvc.show({ type: 'error', ...this.config.offline, autoClose: false, filled: true });

    // The toast can also be dismissed by hand, so drop our reference when it goes away.
    toast._closeRequested.pipe(first()).subscribe(() => (this.offlineToast = undefined));
    this.offlineToast = toast;
  }

  private showBackOnline() {
    this.offlineToast?.close();
    this.offlineToast = undefined;

    if (!this.wasOffline) return;
    this.wasOffline = false;

    this.toastSvc.show({
      type: 'success',
      ...this.config.online,
      closeDelay: this.config.onlineToastDelay,
      filled: true,
    });
  }
}
